import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';

/**
 * Configuration Versions — operator-facing UI for the Control Plane's DRAFT -> VALIDATE ->
 * PUBLISH -> VERSION -> ROLLBACK lifecycle (packages/config-backend/src/routes/configVersions.js).
 * This closes P4 of docs/BIDDIGI_PRODUCT_ROADMAP.md: the lifecycle API has existed and been
 * tested since Phase 1 Step 2, and Prebid.js has been able to *consume* a published version since
 * Phase 2A — but until this tab, nothing let an operator actually drive it without a raw API call,
 * which is exactly the kind of "normal operator workflow requires backend/API access" gap this
 * dashboard exists to close. Built entirely on the existing API: no new config-backend route, no
 * schema change, no new Publisher/Property/Ad-Unit model.
 *
 * SAFETY NOTE — read before touching this file: publishing a version replaces what
 * GET /:domain/prebid-config serves this publisher's real Prebid.js wrapper (Phase 2A's
 * resolver). A snapshot's ad units are populated from the properties/ad_unit_refs layer, which
 * config-backend's own write routes keep in sync with `placements` (see src/lib/properties.js) —
 * but this tab adds its own defense-in-depth check regardless: the publish-confirmation dialog
 * compares the draft's PREBID_JS ad-unit count against this publisher's live placement count
 * (a separate, independent read) and BLOCKS publish on a mismatch, not just warns, since that
 * would mean silently serving fewer ad units than the publisher actually has live right now. This
 * matters because — per this project's own audits — no production publisher had ever published a
 * version before this tab existed, so this exact scenario had never been exercised end to end.
 */
export default function ConfigVersionsTab({ publisherId }) {
  const toast = useToast();
  const [versions, setVersions] = useState(null);
  const [activeVersion, setActiveVersion] = useState(undefined); // undefined = loading, null = none published
  const [placementCount, setPlacementCount] = useState(null);
  const [error, setError] = useState(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);
  const [confirmRollback, setConfirmRollback] = useState(null);

  async function load() {
    setError(null);
    try {
      const [list, placements] = await Promise.all([
        api.listConfigVersions(publisherId),
        api.listPlacements(publisherId).catch(() => []),
      ]);
      setVersions(list);
      setPlacementCount(placements.length);
      try {
        setActiveVersion(await api.getActiveConfigVersion(publisherId));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) setActiveVersion(null);
        else throw err;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load configuration versions.');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherId]);

  async function openDetail(id) {
    setDetailId(id);
    setDetailLoading(true);
    try {
      setDetail(await api.getConfigVersion(id));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load version detail.');
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function createDraft() {
    setCreatingDraft(true);
    try {
      const draft = await api.createConfigVersionDraft(publisherId);
      toast.success(`Draft v${draft.version} created from the current live configuration.`);
      await load();
      openDetail(draft.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create draft.');
    } finally {
      setCreatingDraft(false);
    }
  }

  async function validate(version) {
    setBusyId(version.id);
    try {
      const outcome = await api.validateConfigVersion(version.id);
      if (outcome.valid) {
        const w = outcome.warnings.length;
        toast.success(`v${version.version} is valid${w ? ` (${w} warning${w === 1 ? '' : 's'})` : ''}.`);
      } else {
        toast.error(`v${version.version} has ${outcome.errors.length} validation error(s) — open it to see why.`);
      }
      await load();
      if (detailId === version.id) openDetail(version.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not validate.');
    } finally {
      setBusyId(null);
    }
  }

  function prebidJsAdUnitCount(snapshot) {
    return (snapshot?.properties || [])
      .flatMap((p) => p.adUnits || [])
      .filter((u) => u.integrationType === 'PREBID_JS').length;
  }

  async function openPublishConfirm(version) {
    setBusyId(version.id);
    try {
      // Always re-fetch full detail fresh — never trust a possibly-stale list row for the
      // mismatch check or the confirmation dialog's own numbers.
      const full = await api.getConfigVersion(version.id);
      setConfirmPublish(full);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load version.');
    } finally {
      setBusyId(null);
    }
  }

  async function doPublish(version) {
    setBusyId(version.id);
    try {
      await api.publishConfigVersion(version.id);
      toast.success(`v${version.version} is now live for this publisher's Prebid.js config.`);
      setConfirmPublish(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error(`Publish blocked: ${(err.body?.errors || []).length} validation error(s).`);
        setConfirmPublish(null);
        openDetail(version.id);
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Could not publish.');
      }
    } finally {
      setBusyId(null);
    }
  }

  async function doRollback(version) {
    setBusyId(version.id);
    try {
      const restored = await api.rollbackConfigVersion(publisherId, version.id);
      toast.success(`Rolled back: v${restored.version} (a copy of v${version.version}) is now live.`);
      setConfirmRollback(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error(`Rollback blocked: that old snapshot no longer validates (${(err.body?.errors || []).length} error(s)).`);
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Could not roll back.');
      }
      setConfirmRollback(null);
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <EmptyState title="Couldn't load configuration versions" message={error} />;
  if (versions === null) return <LoadingState label="Loading configuration versions" />;

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Live configuration</h2>
          <span className="card__hint">What Prebid.js actually serves for this publisher right now</span>
        </div>
        <div style={{ padding: '0 20px 20px' }}>
          {activeVersion === undefined ? (
            <LoadingState label="Loading" />
          ) : activeVersion === null ? (
            <p className="card__hint">
              No configuration version has ever been published for this publisher — Prebid.js is
              served from the legacy live tables directly (the same data this dashboard's Settings,
              Placements &amp; Floors, and Bidders tabs edit). That's the normal, safe state;
              publishing a version here is optional, not required for this publisher to keep working.
            </p>
          ) : (
            <>
              <div>
                <strong>v{activeVersion.version}</strong> — published{' '}
                {activeVersion.publishedAt ? new Date(activeVersion.publishedAt).toLocaleString() : '—'}
              </div>
              <div className="card__hint">
                {prebidJsAdUnitCount(activeVersion.snapshot)} PREBID_JS ad unit(s) in this snapshot
                {placementCount !== null && prebidJsAdUnitCount(activeVersion.snapshot) !== placementCount && (
                  <span style={{ color: 'var(--status-error-fg)' }}>
                    {' '}— this publisher currently has {placementCount} live placement(s). Create a
                    fresh draft to bring the published config back in sync.
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="card card--flush">
        <div className="card__header card__header--padded">
          <h2 className="card__title">Version history</h2>
          <button className="btn btn--primary btn--sm" onClick={createDraft} disabled={creatingDraft}>
            {creatingDraft ? 'Creating…' : '+ Create draft from live config'}
          </button>
        </div>

        {versions.length === 0 ? (
          <div className="card__padded">
            <EmptyState
              title="No configuration versions yet"
              message="Create a draft to snapshot this publisher's current live configuration, then validate and publish it."
              action={
                <button className="btn btn--primary" onClick={createDraft} disabled={creatingDraft}>
                  + Create draft from live config
                </button>
              }
            />
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Validation</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id}>
                    <td className="cell-title">
                      <button className="link-button" onClick={() => openDetail(v.id)}>
                        v{v.version}
                      </button>
                    </td>
                    <td>
                      <StatusBadge status={v.status} />
                    </td>
                    <td className="cell-muted">
                      {v.validatedAt
                        ? (v.validationErrors || []).length === 0
                          ? `valid${(v.validationWarnings || []).length ? ` (${v.validationWarnings.length} warning${v.validationWarnings.length === 1 ? '' : 's'})` : ''}`
                          : `${v.validationErrors.length} error(s)`
                        : 'not validated'}
                    </td>
                    <td className="cell-muted">{new Date(v.createdAt).toLocaleString()}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions">
                        {v.status === 'draft' && (
                          <>
                            <button className="btn btn--ghost btn--sm" disabled={busyId === v.id} onClick={() => validate(v)}>
                              {busyId === v.id ? 'Working…' : 'Validate'}
                            </button>
                            <button className="btn btn--primary btn--sm" disabled={busyId === v.id} onClick={() => openPublishConfirm(v)}>
                              Publish
                            </button>
                          </>
                        )}
                        {(v.status === 'published' || v.status === 'archived') && (
                          <button className="btn btn--ghost btn--sm" disabled={busyId === v.id} onClick={() => setConfirmRollback(v)}>
                            Roll back to this
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {detailId && (
        <Modal
          title={detail ? `Configuration v${detail.version}` : 'Loading…'}
          onClose={() => {
            setDetailId(null);
            setDetail(null);
          }}
          width={640}
        >
          {detailLoading || !detail ? <LoadingState label="Loading version" /> : <VersionDetail detail={detail} />}
        </Modal>
      )}

      {confirmPublish && (
        <PublishConfirmModal
          version={confirmPublish}
          placementCount={placementCount}
          busy={busyId === confirmPublish.id}
          onCancel={() => setConfirmPublish(null)}
          onConfirm={() => doPublish(confirmPublish)}
        />
      )}

      {confirmRollback && (
        <Modal
          title={`Roll back to v${confirmRollback.version}?`}
          onClose={() => setConfirmRollback(null)}
          width={480}
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setConfirmRollback(null)}>
                Cancel
              </button>
              <button className="btn btn--primary" disabled={busyId === confirmRollback.id} onClick={() => doRollback(confirmRollback)}>
                {busyId === confirmRollback.id ? 'Rolling back…' : 'Roll back'}
              </button>
            </>
          }
        >
          <p>
            This copies v{confirmRollback.version}&rsquo;s snapshot into a brand-new version and
            publishes that — v{confirmRollback.version} itself is never modified. Whatever is
            currently live for this publisher is replaced immediately.
          </p>
        </Modal>
      )}
    </div>
  );
}

function PublishConfirmModal({ version, placementCount, busy, onCancel, onConfirm }) {
  const adUnitCount = (version.snapshot.properties || [])
    .flatMap((p) => p.adUnits || [])
    .filter((u) => u.integrationType === 'PREBID_JS').length;
  const mismatch = placementCount !== null && adUnitCount !== placementCount;
  const errors = version.validationErrors || [];
  const warnings = version.validationWarnings || [];
  const blocked = mismatch || errors.length > 0;

  return (
    <Modal
      title={`Publish v${version.version}?`}
      onClose={onCancel}
      width={520}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy || blocked} onClick={onConfirm}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </>
      }
    >
      <p>This immediately replaces what Prebid.js serves for this publisher&rsquo;s real config.</p>
      <ul className="plain-list">
        <li>{adUnitCount} PREBID_JS ad unit(s) in this draft</li>
        <li>{(version.snapshot.floors || []).length} floor(s)</li>
        <li>{(version.snapshot.bidders || []).length} enabled bidder(s)</li>
        <li>{(version.snapshot.demandPartners || []).length} enabled server-side demand partner(s) — also governs inbound oRTB/VAST/CTV for this publisher, not just Prebid.js</li>
      </ul>
      {mismatch && (
        <div className="form-error">
          This draft has {adUnitCount} PREBID_JS ad unit(s), but this publisher currently has{' '}
          {placementCount} live placement(s) — publishing is blocked until they match. Cancel and
          create a fresh draft instead.
        </div>
      )}
      {errors.length > 0 && (
        <div className="form-error">
          {errors.length} validation error(s) — publish is blocked until these are fixed:
          <ul className="plain-list">
            {errors.map((e, i) => (
              <li key={i}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="card__hint">
          {warnings.length} warning(s) (non-blocking):
          <ul className="plain-list">
            {warnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

function VersionDetail({ detail }) {
  const s = detail.snapshot || {};
  const adUnits = (s.properties || []).flatMap((p) => (p.adUnits || []).map((u) => ({ ...u, propertyName: p.name })));
  return (
    <div className="stack">
      <div>
        <StatusBadge status={detail.status} /> &middot; created {new Date(detail.createdAt).toLocaleString()}
        {detail.publishedAt && <> &middot; published {new Date(detail.publishedAt).toLocaleString()}</>}
      </div>
      <div>
        <h3 className="placement-expansion__heading">Ad units ({adUnits.length})</h3>
        {adUnits.length === 0 ? (
          <p className="card__hint">None</p>
        ) : (
          <>
            {/* Runtime ad-unit/placement control-plane resolution (2026-08-30): for VAST/CTV
                specifically, this status is what auction-service's adUnitConfig.js resolver now
                actually enforces at serving time once this version is published -- authoritative
                for this exact placement/ctv-ad-unit even if the live placements/ctv_ad_units row
                is later changed without a republish. Shown here so an operator publishing this
                version can see exactly what will (and will not) serve, not just what integration
                type/media type each ad unit is. */}
            <p className="card__hint">
              Status shown here is what will actually be enforced at VAST/CTV serving time once published — a live-table change after publishing this version has no effect until the next publish.
            </p>
            <ul className="plain-list">
              {adUnits.map((u) => (
                <li key={u.id}>
                  {u.propertyName} / {u.code || `(ad_unit_ref ${u.id})`} — {u.mediaType} via {u.integrationType}
                  {' '}<StatusBadge status={u.status} />
                  {u.placementId != null && <span className="card__hint"> (placement {u.placementId})</span>}
                  {u.ctvAdUnitId != null && <span className="card__hint"> (CTV ad unit {u.ctvAdUnitId})</span>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <div>
        <h3 className="placement-expansion__heading">Floors ({(s.floors || []).length})</h3>
        <ul className="plain-list">
          {(s.floors || []).map((f) => (
            <li key={f.id ?? `${f.mediaType}-default`}>
              {f.mediaType}: {f.cpm} {f.currency}
              {f.placementId ? ` (placement ${f.placementId})` : ' (publisher default)'}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="placement-expansion__heading">Enabled bidders ({(s.bidders || []).length})</h3>
        <p className="card__hint">{(s.bidders || []).map((b) => b.code).join(', ') || 'None'}</p>
      </div>
      <div>
        <h3 className="placement-expansion__heading">
          Enabled demand partners ({(s.demandPartners || []).length})
        </h3>
        {/* Codes/names/margin/timeout only — never params. Same convention as "Enabled bidders"
            above: credentials are configured on the Demand Connectivity tab (MaskedParamsEditor,
            never re-displays a saved value), not read back here. This is the server-side demand
            that backs the `biddigi` bidder AND every inbound oRTB/VAST/CTV auction for this
            publisher — distinct from the client-side "Enabled bidders" list above. */}
        {(s.demandPartners || []).length === 0 ? (
          <p className="card__hint">None</p>
        ) : (
          <ul className="plain-list">
            {s.demandPartners.map((p) => (
              <li key={p.id}>
                {p.name} ({p.code}) — {p.marginPct}% margin, {p.timeoutMs}ms timeout
                {p.params !== null ? ', publisher-specific credentials' : ', network-default credentials'}
              </li>
            ))}
          </ul>
        )}
      </div>
      {(detail.validationErrors || []).length > 0 && (
        <div className="form-error">
          <strong>Validation errors:</strong>
          <ul className="plain-list">
            {detail.validationErrors.map((e, i) => (
              <li key={i}>{e.message}</li>
            ))}
          </ul>
        </div>
      )}
      {(detail.validationWarnings || []).length > 0 && (
        <div className="card__hint">
          <strong>Warnings:</strong>
          <ul className="plain-list">
            {detail.validationWarnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
